import React, { Suspense, useEffect, useMemo, useState } from "react";

import { EngagementCohortsInsightModule } from "../../../../../../components/AIModules/EngagementCohortsInsight";
import EmptyChart from "../../../../../../components/EmptyChart";
import Spinner from "../../../../../../components/Spinner/QuarterCircle";
import Text from "../../../../../../components/Text";
// components
import { CardBody, CardHeader } from "../../../../components/Card";
import HeatmapTable, { IColumnSort, IHeatmapColumn } from "../../../../components/HeatmapTable";

import { ICohortFactorSummaryPoint } from "react/pages/Engagement/schema";

// store
import useStore from "../../../../store";

// api hooks
import { useCohortFactorSummary, useCohorts } from "../../../../../../api/hooks";
import useCohortTableColumns from "./useCohortTableColumns";

import { ICohortModel } from "../../../../../../api/services";

// utils
import downloadCSV from "../../../../../../utils/downloadCSV";

// config
import { defaultSelectColor } from "../../../../Config";
// styles
import css from "./CohortFactorHeatmap.module.scss";

/**
 * This component is used to display a heatmap of the factors, as well
 * as the eNPS and overall score for each cohort, and an "All Employees" row
 * for displaying the cohort agnostic average across all employees.
 */
const CohortFactorHeatmap = () => {
	// set up
	const [filter] = useStore((state) => [state.filter]);
	const [isCohortInsightsPanelOpen, setIsCohortInsightsPanelOpen] = useState(false);
	const [selectedCohort, setSelectedCohort] = useState<string | null>(null);

	// state used for sorting between two tables
	const [sortedCohortIDs, setSortedCohortIDs] = useState<string[]>([]);
	const [sortedColumn, setSortedColumn] = useState<IColumnSort>({ columnId: "cohorts", direction: "asc" });

	// api hooks
	const { data: factorsData, isLoading } = useCohortFactorSummary({
		startDate: filter.startDate,
		endDate: filter.endDate,
		include_overall: "true",
	});
	const { data: cohortMetaData, isLoading: isCohortDataLoading } = useCohorts();

	// use effect hook to set the column sort as soon as the data is available, and filter out cohorts without data
	useEffect(() => {
		const sortedCohortIDs = cohortMetaData?.cohorts.map((co) => co._id.toString()) || [];
		setSortedCohortIDs(["all", ...sortedCohortIDs]);
	}, [cohortMetaData]);

	const [factorsTableColumns, rightTableColumns]: [IHeatmapColumn[], IHeatmapColumn[]] = useCohortTableColumns({
		cohortMetaData: cohortMetaData || ({ cohorts: [] } as { cohorts: ICohortModel[]; [key: string]: unknown }),
		factorsData: factorsData || ([] as ICohortFactorSummaryPoint[]),
	});

	const selectedCohortData = useMemo(() => {
		const foundCohort = cohortMetaData?.cohorts.find((cohort) => cohort._id === selectedCohort);
		const cohortData = foundCohort
			? { _id: foundCohort?._id, name: foundCohort?.name, description: foundCohort?.description }
			: null;
		return cohortData;
	}, [cohortMetaData, selectedCohort]);

	// methods
	const onSortingChanged = (sorting: string[], sortedColumn: IColumnSort) => {
		setSortedCohortIDs(sorting);
		setSortedColumn(sortedColumn);
	};

	const getCSVContent = (data: IHeatmapColumn[]) => {
		const csvColumns = [...data.map((d) => `"${d.label}"`)].join(",");

		const csvRows = sortedCohortIDs
			.map((key) => {
				const row = [...data.map((d) => `"${d.data[key] || ""}"`)].join(",");
				return row;
			})
			.join("\n");
		const csvContent = `${csvColumns}\n${csvRows}`;

		return csvContent;
	};

	const selectCSVDownloadName = () => {
		const date = new Date().toDateString();

		let timePeriod: string | null = null;

		switch (filter.value) {
			case "30":
				timePeriod = "30 Days";
				break;
			case "90":
				timePeriod = "90 Days";
				break;
			case "year":
				timePeriod = "12 Months";
				break;
			case "all":
				timePeriod = "Overall";
				break;
			default:
				break;
		}

		return `PerformYard-Engagement-Cohorts-${timePeriod ? timePeriod : ""}-${date}.csv`;
	};

	const handleOptionSelect = (option: string, rowID: string) => {
		if (option === "ai_analysis") {
			setSelectedCohort(rowID);
			setIsCohortInsightsPanelOpen(true);
		}
	};

	const hasData = useMemo(() => {
		return factorsData?.length && factorsData.length > 0 && cohortMetaData?.cohorts?.length;
	}, [factorsData, cohortMetaData]);

	return (
		<>
			<CardHeader
				title={
					<span>
						<Text element="span" size="paragraph-2" weight="semi-bold">
							Cohort Satisfaction
						</Text>
					</span>
				}
				info="This chart displays satisfaction factors by cohort over the selected time period."
				options={
					hasData // only show the download csv option if there is data
						? [
								{
									id: "download-csv",
									label: "Download CSV",
									action: () => {
										// navigate to /engagement/factors page
										const csvContent = getCSVContent([
											...factorsTableColumns,
											...rightTableColumns,
										]);
										const filename = selectCSVDownloadName();
										downloadCSV(csvContent, filename);
									},
								},
							]
						: null
				}
			/>
			<CardBody>
				<div className={css.cardBody}>
					{isLoading || isCohortDataLoading ? (
						<div className={css.emptyChart}>
							<Spinner />
						</div>
					) : (
						<>
							{!hasData ? (
								<div className={css.emptyChart}>
									<EmptyChart type="heatmap" />
								</div>
							) : (
								<div>
									<HeatmapTable
										rowIds={sortedCohortIDs}
										// keeps the "All Employees" row at the top of the table
										frozenTopRow={true}
										// the column that is currently sorted, able to be passed to the other table
										sortedColumn={sortedColumn}
										// when the sorting changes, update the sorted column and cohort ids
										onSortingChanged={onSortingChanged}
										// the columns to display in this table (labels, factors, eNPS and overall satisfaction)
										columns={[...factorsTableColumns, ...rightTableColumns]}
										// the color function to use for the cell, creates the heatmap effect
										defaultSelectBackgroundColor={defaultSelectColor}
										// hndle selection of option
										onOptionSelect={handleOptionSelect}
									/>
								</div>
							)}
						</>
					)}
				</div>
			</CardBody>
			{isCohortInsightsPanelOpen && (
				<Suspense
					fallback={
						<div className="fixed bottom-0 right-0 z-50 m-4 rounded bg-white p-4 shadow">
							<p className="text-sm text-neutral-600">Loading analysis...</p>
						</div>
					}
				>
					<EngagementCohortsInsightModule
						isOpen={isCohortInsightsPanelOpen}
						onOpenChange={setIsCohortInsightsPanelOpen}
						cohortData={selectedCohortData}
						factorsData={factorsData ?? []}
						filter={filter}
					/>
				</Suspense>
			)}
		</>
	);
};

export default CohortFactorHeatmap;
